import { useMemo } from 'react';
import { type Nullable } from '@direct-frontend/stdlib';
import { useField, useFormContext, useFormInstance } from '@adv-frontend/form-kit';
import { OrderDeliveryMethods, OrderDeliveryServices } from '@/shared/api/order';
import { type ArrayIntervalFieldValue } from '@/shared/components/IntervalInput';
import { ORDER_FORM_ID } from '../../constants';
import type { PickupAddressFieldValue } from '../../model/forms/fieldPickupAddress';
import { orderForm } from '../../model/forms/orderForm';

/**
 * Контекст, где проверяем валидность
 * form - вся форма
 * pickup - селект ПВЗ
 * delivery - селект интервалов
 */
type FieldType = 'form' | 'pickup' | 'delivery';

interface GetIsInvalidOzonUserParams {
    isUserStatusInvalid: boolean;
    method: Nullable<string>;
    type: FieldType;
    fieldAddressPickupValue: PickupAddressFieldValue;
    courierIntervals: ArrayIntervalFieldValue;
}

/*
 * Определяет, валиден ли статус Ozon ID у пользователя для текущего контекста (FieldType).
 */
export const getIsInvalidOzonUser = (params: GetIsInvalidOzonUserParams) => {
    const { isUserStatusInvalid, method, type, fieldAddressPickupValue, courierIntervals } = params;

    if (!isUserStatusInvalid) {
        return false;
    }

    if (method === OrderDeliveryMethods.SELF_PICK_UP) {
        return false;
    }

    if (method === OrderDeliveryMethods.PICKUP_POINT) {
        return (
            fieldAddressPickupValue.serviceType === OrderDeliveryServices.OZON && (type === 'pickup' || type === 'form')
        );
    }

    if (method === OrderDeliveryMethods.COURIER) {
        return courierIntervals.some(
            (item) =>
                item.intervalField.type === 'interval' && item.intervalField.serviceType === OrderDeliveryServices.OZON
        );
    }

    return false;
};

interface UseIsInvalidOzonUserProps {
    type: FieldType;
    chunkId?: number;
}

/*
 * Возвращает true, если у пользователя невалидный Ozon ID и по сервису доставки для текущего способа доставки:
 * для самовывоза всегда false; для ПВЗ - true, если выбран ПВЗ OZON, для курьера — true, если хотя бы один чанк с OZON.
 *
 * @param props.type — контекст поля: 'form' | 'pickup' | 'delivery'
 * @param props.chunkId — опционально: id чанка для проверки интервалов курьера (если не передан — учитываются все чанки)
 */
export const useIsInvalidOzonUser = (props: UseIsInvalidOzonUserProps): boolean => {
    const { type, chunkId } = props;

    const { fields } = useFormInstance({ form: orderForm, id: ORDER_FORM_ID });
    const context = useFormContext({ form: orderForm, id: ORDER_FORM_ID });
    const fieldDeliveryMethod = useField(fields.deliveryMethod);
    const fieldAddressPickup = useField(fields.addressPickup);
    const fieldDeliveryCourierInterval = useField(fields.deliveryCourierInterval);

    const isUserStatusInvalid = context.order.client.need_ozon_registration;

    const method = fieldDeliveryMethod.value;

    const courierIntervals = useMemo(
        () =>
            typeof chunkId === 'number'
                ? fieldDeliveryCourierInterval.value.filter((interval) => interval.chunkId === chunkId)
                : fieldDeliveryCourierInterval.value,
        [chunkId, fieldDeliveryCourierInterval.value]
    );

    return getIsInvalidOzonUser({
        type,
        isUserStatusInvalid,
        fieldAddressPickupValue: fieldAddressPickup.value,
        courierIntervals,
        method,
    });
};
